<script setup lang="ts">
import { useHead } from '@unhead/vue'
import CallToAction from '../components/CallToActionContactUs.vue'
import DascoResponsiveVideo from '../components/DascoResponsiveVideo.vue'
import { buildLinkYoutube } from '../utils/buildLinkYoutube'

// HEADER
useHead({
  title: 'Pressure Gauge Venting (Burping)',
  meta: [
    {
      name: 'description',
      content: 'Learn how to easily vent/burp liquid-filled gauges'
    }
  ]
})
</script>

<template>
  <section class="p-2 lg:p-6 surface-section border-round">
    <div class="grid">
      <div class="col-12 text-center">
        <h1 class="text-3xl md:text-5xl text-red-500 uppercase font-medium m-0 p-0">
          Pressure Gauge Venting (Burping)
        </h1>
      </div>
      <div class="col-12 md:col-6">
        <div class="text-4xl text-primary">
          It is common for gauges to be off zero due to build-up of case pressure
        </div>
        <div class="mt-2 mb-4 text-xl">
          Increase confidence in your instruments by periodically venting your liquid-filled gauges.
        </div>

        <div class="text-2xl text-primary">
          Do I need to vent my gauge?
        </div>
        <p>
          If you have a dry gauge… no need to worry about venting.
        </p>
        <p>
          Venting is only for liquid-filled gauges and is often recommended directly after installation.
        </p>
        <p>
          Stressful environments, where vibration and shock are present, may often require liquid-filled gauges. The
          liquid medium reduces that vibration being transferred to the instruments internals and is commonly referred to
          as pulsation dampening.
        </p>
        <p>
          This dampening protects the internal parts of the gauge and also helps steady the needle.
        </p>
        <p>
          <strong>However, sometimes a liquid-filled gauge will just not return to zero.</strong>
        </p>
        <div class="text-2xl text-primary mb-2">
          In a hurry?
        </div>
        <div class="border-round p-3 text-2xl surface-100 flex align-items-start">
          <fa-icon
            icon="fa fa-info-circle"
            class="fa-fw text-4xl text-red-500 mr-3"
          />
          <p class="m-0">
            <a
              :href="buildLinkYoutube({ src: 'dVmWk4b-vY4' })"
              class="text-red-500 text-decoration-none"
              rel="noopener"
              target="_blank"
            >
              Watch our video
              <fa-icon
                icon="fa fa-external-link"
                class="text-lg"
              />
            </a> on how to vent your liquid-filled gauge. Venting is easy, and it only takes seconds.
          </p>
        </div>
        <div class="text-2xl text-primary mt-4 mb-2">
          Why does it happen?
        </div>
        <p>
          The simple answer is… variations in temperature cause the case and internals to contract and expand. This can
          create a pressure difference between the inside of the case and the atmosphere outside the gauge.
        </p>
        <p>
          This pressure difference can occur during shipping and even after installation in an environment that
          experiences extreme temperature differences.
        </p>

        <div class="text-2xl text-primary mt-4 mb-2">
          Solutions:
        </div>
        <p>
          Often the simple fix is to alleviate the case pressure by venting the gauge, also known as "burping" the gauge.
        </p>
        <p>
          There are typically two types of fill plugs that you will come across on your liquid-filled gauges.
        </p>
        <div class="grid">
          <div class="col-12 lg:col-6">
            <div class="text-xl font-bold">
              Solid Rubber
            </div>
            <div class="text-center p-2">
              <img
                alt="Venting Ashcroft 25 1009SWL Pressure Gauge"
                class="border-round border-1 border-300 w-full"
                src="/images/venting_ashcroft_25_1009SW_LCR.jpg"
                style="max-width: 600px;"
              >
            </div>
          </div>
          <div class="col-12 lg:col-6">
            <div class="text-xl font-bold">
              Lever Type
            </div>
            <div class="text-center p-2">
              <img
                alt="Venting WIKA 213.53 Pressure Gauge"
                class="border-round border-1 border-300 w-full"
                src="/images/venting_wika_213_53_25BM.jpg"
                style="max-width: 600px;"
              >
            </div>
          </div>
        </div>
      </div>
      <div class="col-12 md:col-5 md:col-offset-1">
        <div class="p-4">
          <dasco-responsive-video
            class="border-round"
            src="dVmWk4b-vY4"
            service="youtube"
            :width="1280"
            :height="720"
          />
          <call-to-action
            class="mt-4"
            text="Gauge still not returning to zero?"
            text-secondary="We are here to assist"
          />
        </div>
      </div>
    </div>
  </section>
</template>
